
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';

interface CountyDistributionChartProps {
  dateRange: { from: Date | undefined; to: Date | undefined };
}

const COLORS = ['#00C49F', '#FFBB28', '#FF8042', '#0088FE', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

export function CountyDistributionChart({ dateRange }: CountyDistributionChartProps) {
  const endDate = dateRange.to || new Date();
  const startDate = dateRange.from || new Date(endDate.getFullYear() - 1, endDate.getMonth(), endDate.getDate());
  
  const { data: usersCertificates, isLoading } = useQuery({
    queryKey: ['countyCertificates', startDate, endDate],
    queryFn: async () => {
      // Join certificates with profiles to get county information
      const { data: certificateUsers, error } = await supabase
        .from('certificates')
        .select(`
          id,
          user_id,
          profiles!certificates_user_id_fkey (
            county
          )
        `)
        .gte('issued_at', startDate.toISOString())
        .lte('issued_at', endDate.toISOString());
      
      if (error) throw error;
      return certificateUsers;
    },
  });
  
  // Process the data for the chart
  const chartData = React.useMemo(() => {
    if (!usersCertificates) return [];
    
    const countyMap: Record<string, number> = {};
    
    usersCertificates.forEach(cert => {
      // The certificate has a profile with county info
      const county = (cert as any).profiles?.county || 'Unknown';
      countyMap[county] = (countyMap[county] || 0) + 1;
    });
    
    // Convert the map to an array of objects
    return Object.entries(countyMap)
      .map(([county, count]) => ({ county, count }))
      .sort((a, b) => b.count - a.count); // Sort by count in descending order
  }, [usersCertificates]);
  
  if (isLoading) {
    return <div>Loading chart data...</div>;
  }
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ county, percent }) => `${county}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="count"
          nameKey="county"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value, name, props) => [`${value} certificates`, props.payload.county]} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
